import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import * as _ from 'lodash';
import { BaseComponent } from '../../Library/BaseComponent';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { MasterDataService } from '../../Services/MasterDataService';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StaffHealthDataService } from '../../Services/StaffHealthDataService';
import { AddEditQuestionSet, AddEditQuestion, QuestionSetInsertUpdateRequest } from '../../Models/RequestModels';
import { PropertyBasicInfo } from '../../Models/GlobalEntities';
import { EntityCreationMode, QuestionSetStatus, AuthActionCodes } from '../../Models/EnumModels';
import { QuestionSetDetail, StaffQuestionaireTemplate, BaseResponse } from '../../Models/ResponseModels';
import { UUID } from 'angular2-uuid';

type EditModes = 'edit' | 'clone' | undefined;

@Component({
    selector: 'app-add-edit-question-set',
    templateUrl: './add-edit-question-set.component.html',
})
export class AddEditQuestionSetComponent extends BaseComponent implements OnInit {

    @Input() DisplayMode: EntityCreationMode = EntityCreationMode.Page;

    @Input() EditMode: EditModes;

    @Input() set EditModel(editModel: StaffQuestionaireTemplate) {
        if (this.Model && editModel && this.Model.Id == editModel.Id) {
            return;
        }
        this.initiateEdit(editModel);
    }

    @Output() onAction = new EventEmitter();

    Model: AddEditQuestionSet;
    EditReadModel?: QuestionSetDetail;

    lstProperties: Array<PropertyBasicInfo> = [];
    DisplayModes = EntityCreationMode;

    constructor(
        public translate: TranslateService,
        public toastr: ToastrService,
        public route: ActivatedRoute,
        public router: Router,
        public masterService: MasterDataService,
        public modalService: NgbModal,
        protected staffHealthService: StaffHealthDataService) {

        super(translate, toastr, route, router, masterService);

        this.lstProperties = this.masterService.GetProperties_Action(
            AuthActionCodes.StaffHealth_MANAGE
        );
        var selectedPropertyId: number;
        if (
            this.lstProperties.findIndex(
                a => a.PropertyId == this.masterService.ActivePropertyID
            ) > 0
        )
            selectedPropertyId = this.masterService.ActivePropertyID;
        else {
            if (this.lstProperties.length > 0)
                selectedPropertyId = this.lstProperties[0].PropertyId;
        }

        this.Model = AddEditQuestionSet.reset(selectedPropertyId);
        this.didTakeQuestionAction({ action: 'insert' });
    }

    ngOnInit() { }

    initiateEdit(editModel: StaffQuestionaireTemplate) {
        if (!editModel) {
            return;
        }
        // get latest data
        const propertyId = editModel.PropertyId;
        const templateId = editModel.Id;
        if (!propertyId || !templateId) {
            return;
        }
        this.EditReadModel = null;
        this.staffHealthService.GetQuestionaireById(propertyId, templateId, true)
            .then(data => {
                if (data.HasError) {
                    this.FromBaseResponse(data);
                    return;
                }
                this.EditReadModel = data.Result;
                this.populateModelFrom(data.Result, this.EditMode);
            }, error => {
                console.error(error);
            });
    }

    populateModelFrom(editModel: QuestionSetDetail, editMode: EditModes = undefined) {
        if (!editModel) {
            return;
        }
        const questions = editModel.Questions || [];
        const template = editModel.StaffQuestionaireTemplate;
        if (!template) {
            return
        }
        const model = AddEditQuestionSet.reset(template.PropertyId);
        if (editMode != 'clone') {
            model.Id = template.Id;
        }
        model.PropertyId = template.PropertyId;
        model.QuestionSetId = UUID.UUID();
        model.QuestionSetName = (editMode == 'clone' ? 'Copy:' : '') + template.TemplateName;
        model.Questions = questions.map(q => {
            const question = AddEditQuestion.reset();
            question.AnswerOption = q['AnswerOption'];
            question.IsAnswerRequired = q.IsAnswerRequired;
            question.QuestionDesc = q.QuestionText;
            question.QuestionId = q.UniqueId;
            return question
        });
        this.Model = model;
    }

    // track by 
    trackQuestionFunction(index, item) {
        if (!item) {
            return null;
        }
        return item.QuestionId
    }
    // toggle isSelected flag on questions
    didSelectQuestion(event, question: AddEditQuestion) {
        let questions = this.Model.Questions || [];
        if (question.IsSelected) {
            return;
        }
        questions = questions.map((q) => {
            q.IsSelected = false;
            return q;
        });
        question.IsSelected = true;
        this.Model.Questions = questions;
        event.preventDefault();

    }

    didTakeQuestionAction(event) {
        const { action, question } = event;
        if (!action) {
            return;
        }
        let questions = this.Model.Questions || [];
        const index = question && _.findIndex(questions, { QuestionId: question.QuestionId });

        switch (action) {
            case 'insert':
                questions.splice(index >= 0 ? index + 1 : 0, 0, AddEditQuestion.reset());
                break;
            case 'delete':
                index >= 0 && questions.splice(index, 1);
                break;
        }
        this.Model.Questions = questions;
    }

    didSubmit(comboAction = false) {
        this.ClearErrors();
        const errors = this.validate();
        if (errors.length) {
            // handle errors
            console.warn(errors);
            errors.forEach(e => this.AddError(e));
            return;
        }
        const userId = this.masterService.LoggedInUserData.StaffId;
        const request = QuestionSetInsertUpdateRequest.from(this.Model)
        if (request) {
            // update userid
            request.QuestionInsertUpdateRequests = request.QuestionInsertUpdateRequests.map(q => {
                q.CreatedBy = userId;
                return q;
            });
            // make request
            return this.staffHealthService.InsertUpdateStaffHealthQuestions(request)
                .then((data) => {
                    console.log(data);
                    this.FormErrors = data.Errors;
                    if (!data.HasError) {
                        this.didResetModel();
                        if (!comboAction) {
                            this.toastr.success("Questionnaire created successfully");
                            this.onAction.emit('questionnaire_submit_success');
                        }
                    } else {
                        this.FromBaseResponse(data);
                    }
                    return data;
                }, console.error)
        }
    }

    validate(): string[] {
        const errors = [];
        // no model - unknown
        if (!this.Model) {
            errors.push('ERR.ERR_SERVER_UNKNOWN_ERR');
            return errors;
        }
        // no property
        if (!this.Model.PropertyId) {
            errors.push('ERR.ERR_SELECT_PROPERTY');
        }
        // no title
        if (!this.Model.QuestionSetName) {
            errors.push('ERR.ERR_ENTER_QUESTIONNARE_NAME');
        }
        // no question or empty question
        if (!this.Model.Questions || this.Model.Questions.length == 0) {
            errors.push('ERR.ERR_QUESTIONNAIRE_EMPTY');
        } else {
            let allValidQs = true;
            this.Model.Questions.forEach(q => {
                if (!allValidQs) {
                    return;
                }
                if (!q.QuestionDesc || q.QuestionDesc.length == 0) {
                    errors.push('ERR.ERR_ENTER_QUESTION_DESC');
                    allValidQs = false;
                }
                // no response
                if (!q.AnswerOption || !q.AnswerOption.type) {
                    errors.push('ERR.ERR_CONFIG_QUESTION_RESPONSE');
                    allValidQs = false;
                }
            });
        }
        

        return errors;

    }

    didSaveActivate() {
        const savePromise = this.didSubmit(true);
        if (!savePromise) {
            return;
        }
        Promise
            .resolve(savePromise)
            .then((data: BaseResponse) => {
                if (!data || data.HasError) {
                    return;
                }
                const { QuestionaireId } = data['Result'];
                if (QuestionaireId) {
                    this.staffHealthService.ChangeQuestionaireStatus({
                        PropertyId: this.Model.PropertyId,
                        QuestionaireId: QuestionaireId,
                        Status: QuestionSetStatus.Active
                    })
                        .then(data => {
                            if (data.HasError) {
                                this.FromBaseResponse(data);
                                return;
                            }
                            this.toastr.success('Questionnaire status updated successfully');
                            this.onAction.emit('questionnaire_activate_success');
                        }, error => {
                            this.AddError('ERR.ERR_SERVER_UNKNOWN_ERR');
                            console.error(error);
                        })
                }

            });
    }

    didResetModel() {
        this.ClearErrors();
        if (this.DisplayMode != EntityCreationMode.Page && this.EditReadModel) {
            this.populateModelFrom(this.EditReadModel, this.EditMode);
            return;
        }
        this.Model = AddEditQuestionSet.reset(this.Model.PropertyId);
        this.didTakeQuestionAction({ action: 'insert' });

    }

    didCancelEdit() {
        this.onAction.emit('questionnaire_edit_cancel')
    }

}
